/**
 * Hand-authored 5x7 bitmap font — the arcade-cabinet typeface that replaces the
 * browser's Verdana. Uppercase + digits + punctuation; lowercase maps to uppercase.
 * Strings are baked into NEAREST textures so they upscale as crisp pixels.
 */
import { Texture } from 'pixi.js';
import { makeCanvas, texFromCanvas } from './pixutil';

export const GLYPH_W = 5;
export const GLYPH_H = 7;
const SPACING = 1; // blank column between glyphs

// Each glyph is 7 rows of 5 chars ('#' = ink). Authored for legibility at 1px.
const G: Record<string, string[]> = {
  A: ['.###.', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  B: ['####.', '#...#', '#...#', '####.', '#...#', '#...#', '####.'],
  C: ['.###.', '#...#', '#....', '#....', '#....', '#...#', '.###.'],
  D: ['###..', '#..#.', '#...#', '#...#', '#...#', '#..#.', '###..'],
  E: ['#####', '#....', '#....', '####.', '#....', '#....', '#####'],
  F: ['#####', '#....', '#....', '####.', '#....', '#....', '#....'],
  G: ['.###.', '#...#', '#....', '#.###', '#...#', '#...#', '.###.'],
  H: ['#...#', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  I: ['.###.', '..#..', '..#..', '..#..', '..#..', '..#..', '.###.'],
  J: ['..###', '...#.', '...#.', '...#.', '#..#.', '#..#.', '.##..'],
  K: ['#...#', '#..#.', '#.#..', '##...', '#.#..', '#..#.', '#...#'],
  L: ['#....', '#....', '#....', '#....', '#....', '#....', '#####'],
  M: ['#...#', '##.##', '#.#.#', '#.#.#', '#...#', '#...#', '#...#'],
  N: ['#...#', '##..#', '#.#.#', '#.#.#', '#..##', '#...#', '#...#'],
  O: ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  P: ['####.', '#...#', '#...#', '####.', '#....', '#....', '#....'],
  Q: ['.###.', '#...#', '#...#', '#...#', '#.#.#', '#..#.', '.##.#'],
  R: ['####.', '#...#', '#...#', '####.', '#.#..', '#..#.', '#...#'],
  S: ['.####', '#....', '#....', '.###.', '....#', '....#', '####.'],
  T: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
  U: ['#...#', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  V: ['#...#', '#...#', '#...#', '#...#', '#...#', '.#.#.', '..#..'],
  W: ['#...#', '#...#', '#...#', '#.#.#', '#.#.#', '##.##', '#...#'],
  X: ['#...#', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '#...#'],
  Y: ['#...#', '#...#', '.#.#.', '..#..', '..#..', '..#..', '..#..'],
  Z: ['#####', '....#', '...#.', '..#..', '.#...', '#....', '#####'],
  '0': ['.###.', '#...#', '#..##', '#.#.#', '##..#', '#...#', '.###.'],
  '1': ['..#..', '.##..', '..#..', '..#..', '..#..', '..#..', '.###.'],
  '2': ['.###.', '#...#', '....#', '..##.', '.#...', '#....', '#####'],
  '3': ['####.', '....#', '....#', '.###.', '....#', '....#', '####.'],
  '4': ['...#.', '..##.', '.#.#.', '#..#.', '#####', '...#.', '...#.'],
  '5': ['#####', '#....', '####.', '....#', '....#', '#...#', '.###.'],
  '6': ['.###.', '#....', '#....', '####.', '#...#', '#...#', '.###.'],
  '7': ['#####', '....#', '...#.', '..#..', '.#...', '.#...', '.#...'],
  '8': ['.###.', '#...#', '#...#', '.###.', '#...#', '#...#', '.###.'],
  '9': ['.###.', '#...#', '#...#', '.####', '....#', '....#', '.###.'],
  ' ': ['.....', '.....', '.....', '.....', '.....', '.....', '.....'],
  '.': ['.....', '.....', '.....', '.....', '.....', '.##..', '.##..'],
  ',': ['.....', '.....', '.....', '.....', '.##..', '.##..', '.#...'],
  '!': ['..#..', '..#..', '..#..', '..#..', '..#..', '.....', '..#..'],
  '?': ['.###.', '#...#', '....#', '..##.', '..#..', '.....', '..#..'],
  ':': ['.....', '.##..', '.##..', '.....', '.##..', '.##..', '.....'],
  ';': ['.....', '.##..', '.##..', '.....', '.##..', '.##..', '.#...'],
  '-': ['.....', '.....', '.....', '#####', '.....', '.....', '.....'],
  '=': ['.....', '.....', '#####', '.....', '#####', '.....', '.....'],
  '+': ['.....', '..#..', '..#..', '#####', '..#..', '..#..', '.....'],
  '/': ['....#', '....#', '...#.', '..#..', '.#...', '#....', '#....'],
  "'": ['..#..', '..#..', '..#..', '.....', '.....', '.....', '.....'],
  '"': ['.#.#.', '.#.#.', '.#.#.', '.....', '.....', '.....', '.....'],
  '(': ['..##.', '.#...', '#....', '#....', '#....', '.#...', '..##.'],
  ')': ['.##..', '...#.', '....#', '....#', '....#', '...#.', '.##..'],
  '%': ['##..#', '##.#.', '..#..', '.#...', '#.##.', '#.##.', '#....'],
  '*': ['.....', '#.#.#', '.###.', '#####', '.###.', '#.#.#', '.....'],
  '×': ['.....', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '.....'],
  '★': ['..#..', '..#..', '#####', '.###.', '.###.', '.#.#.', '#...#'],
  '♥': ['.....', '.#.#.', '#####', '#####', '.###.', '..#..', '.....'],
  '✦': ['..#..', '..#..', '#.#.#', '.###.', '#.#.#', '..#..', '..#..'],
};

function glyph(ch: string): string[] {
  return G[ch] ?? G[ch.toUpperCase()] ?? G['?']!;
}

/** Width in 1x pixels of a rendered string (no trailing spacing). */
export function textWidth(text: string): number {
  if (text.length === 0) return 0;
  return text.length * (GLYPH_W + SPACING) - SPACING;
}

interface TextOpts {
  color?: string;
  /** 1px drop shadow color (offset +1,+1). */
  shadow?: string;
  /** full outline color (8-dir) — for banners. */
  outline?: string;
}

const cache = new Map<string, Texture>();

/** Bake a string into a NEAREST texture at 1x. Cached by content+style. */
export function textTexture(text: string, opts: TextOpts = {}): Texture {
  const color = opts.color ?? '#ffffff';
  const key = `${text}|${color}|${opts.shadow ?? ''}|${opts.outline ?? ''}`;
  const cached = cache.get(key);
  if (cached) return cached;

  const pad = opts.outline ? 1 : opts.shadow ? 1 : 0;
  const w = textWidth(text) + pad;
  const h = GLYPH_H + pad;
  const { canvas, ctx } = makeCanvas(Math.max(1, w), Math.max(1, h));

  const stamp = (col: string, dx: number, dy: number) => {
    ctx.fillStyle = col;
    let cx = dx;
    for (const ch of text) {
      const rows = glyph(ch);
      for (let r = 0; r < GLYPH_H; r++) {
        const row = rows[r]!;
        for (let c = 0; c < GLYPH_W; c++) {
          if (row[c] === '#') ctx.fillRect(cx + c, dy + r, 1, 1);
        }
      }
      cx += GLYPH_W + SPACING;
    }
  };

  if (opts.outline) {
    for (let oy = -1; oy <= 1; oy++)
      for (let ox = -1; ox <= 1; ox++)
        if (ox || oy) stamp(opts.outline, 1 + ox, 1 + oy);
    stamp(color, 1, 1);
  } else if (opts.shadow) {
    stamp(opts.shadow, 1, 1);
    stamp(color, 0, 0);
  } else {
    stamp(color, 0, 0);
  }

  const tex = texFromCanvas(canvas);
  cache.set(key, tex);
  return tex;
}
